import { Home, Users, Phone, Baby } from "lucide-react";
import { cn } from "@/lib/cn";
import type { PersonType } from "@alice/shared";
import {
  PERSON_TYPE_LABELS,
  PERSON_TYPE_DESCRIPTIONS,
} from "@alice/shared";

interface PersonTypeSelectorProps {
  value: PersonType;
  onChange: (type: PersonType) => void;
  disabled?: boolean;
}

const TYPE_ICONS: Record<PersonType, React.ComponentType<{ className?: string }>> = {
  shelter: Home,
  outreach: Users,
  hotline: Phone,
  child: Baby,
};

const TYPE_COLORS: Record<PersonType, string> = {
  shelter: "border-blue-500 bg-blue-50 text-blue-700 ring-blue-500/20",
  outreach: "border-green-500 bg-green-50 text-green-700 ring-green-500/20",
  hotline: "border-amber-500 bg-amber-50 text-amber-700 ring-amber-500/20",
  child: "border-purple-500 bg-purple-50 text-purple-700 ring-purple-500/20",
};

const ALL_TYPES: PersonType[] = ["shelter", "outreach", "hotline", "child"];

export function PersonTypeSelector({
  value,
  onChange,
  disabled,
}: PersonTypeSelectorProps) {
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-foreground">
        Person Type <span className="text-destructive">*</span>
      </label>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {ALL_TYPES.map((type) => {
          const Icon = TYPE_ICONS[type];
          const isSelected = value === type;

          return (
            <button
              key={type}
              type="button"
              disabled={disabled}
              onClick={() => onChange(type)}
              className={cn(
                "flex flex-col items-center gap-2 rounded-lg border-2 p-4 text-center transition-all",
                isSelected
                  ? cn(TYPE_COLORS[type], "ring-2")
                  : "border-border bg-card hover:border-muted-foreground/40 hover:bg-accent/50",
                disabled && "cursor-not-allowed opacity-50",
              )}
            >
              <Icon className={cn("h-6 w-6", isSelected ? "" : "text-muted-foreground")} />
              <div>
                <p className={cn("text-sm font-semibold", !isSelected && "text-foreground")}>
                  {PERSON_TYPE_LABELS[type]}
                </p>
                <p className={cn("mt-0.5 text-xs leading-tight", !isSelected && "text-muted-foreground")}>
                  {PERSON_TYPE_DESCRIPTIONS[type]}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
